/**
 * useKeyboardShortcuts — keyboard shortcuts for layout management
 * date_of_change: 2025-09-04
 * 
 * Provides keyboard shortcuts for cycling sidebar modes and other UI controls:
 * - [ (left bracket): Cycle left sidebar mode
 * - ] (right bracket): Cycle right sidebar mode  
 * - ` (backtick): Cycle topbar mode
 */
import { useEffect } from 'react';
import { useDock } from './useDock';

export function useKeyboardShortcuts() {
  const leftSidebar = useDock('sidebar:left');
  const rightSidebar = useDock('sidebar:right'); 
  const topbar = useDock('topbar');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input
      const target = e.target as HTMLElement;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) {
        return;
      }

      // Prevent default for our shortcuts
      if (e.key === '[' || e.key === ']' || e.key === '`') {
        e.preventDefault();
      }

      switch (e.key) {
        case '[': // Cycle left sidebar mode
          leftSidebar.setMode(prev => {
            switch (prev) {
              case 'expanded': return 'icons';
              case 'icons': return 'collapsed';
              case 'collapsed': return 'expanded';
              default: return 'expanded';
            }
          });
          break;

        case ']': // Cycle right sidebar mode  
          rightSidebar.setMode(prev => {
            switch (prev) {
              case 'expanded': return 'icons';
              case 'icons': return 'collapsed';
              case 'collapsed': return 'expanded';
              default: return 'expanded';
            }
          });
          break;

        case '`': // Cycle topbar mode
          topbar.setMode(prev => {
            switch (prev) {
              case 'expanded': return 'icons';
              case 'icons': return 'collapsed';
              case 'collapsed': return 'expanded';
              default: return 'expanded';
            }
          });
          break;

        default:
          break;
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [leftSidebar, rightSidebar, topbar]);
}